import React, { useRef, useState, useEffect } from "react";
import { Upload, Modal, Button, Progress, message } from "antd";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";
import { CircleNotch, UploadSimpleIcon } from "@phosphor-icons/react";
import showAlertNotification from "../Modal/AlertNotification";
import { useUploadToS3Mutation, useLazyGetPresignedUrlQuery } from "services";
import { AxiosProgressEvent } from "axios";
import { getFilePath } from "utils";
import { UploadImageCommonProps } from "types/image.type";

const getBase64 = (file: File | Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

interface UploadImageButtonProps extends UploadImageCommonProps {
  label?: string;
  loading?: boolean;
  width?: number | string;
  height?: number | string;
  maxCount?: number; // Prop maxCount để tuỳ biến số lượng ảnh
}

const UploadImageButton: React.FC<UploadImageButtonProps> = ({
  imagesData,
  setImagesData,
  isDisabled = false,
  label = "Tải lên QR",
  loading = false,
  width = 84,
  height = 40,
  maxCount = 2,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadToS3] = useUploadToS3Mutation();
  const [getPresignedUrl] = useLazyGetPresignedUrlQuery();
  const uploadingCountRef = useRef(0);
  const shownErrorRef = useRef(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (imagesData?.length > 0 && !initializedRef.current) {
      const newFileList: UploadFile[] = imagesData.map((image, index) => ({
        uid: `existing-${index}`,
        name: image.name || `image-${index}`,
        status: "done",
        url: image.filePath?.startsWith("http")
          ? image.filePath
          : `${getFilePath()}/${image.filePath}`,
        thumbUrl: image.filePath?.startsWith("http")
          ? image.filePath
          : `${getFilePath()}/${image.filePath}`,
      }));

      setFileList(newFileList);
      initializedRef.current = true;
    }
  }, [imagesData]);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(0, maxCount));
  };

  const handleCancel = () => setPreviewOpen(false);

  const handleRemove = (file: UploadFile) => {
    if (file.uid.startsWith("existing-")) {
      const index = parseInt(file.uid.split("-")[1]);
      setImagesData((prev) => {
        return prev
          .filter((_, idx) => idx !== index)
          .map((item, idx) => ({ ...item, order: idx + 1 }));
      });
    } else {
      const removedIndex = fileList.findIndex((item) => item.uid === file.uid);
      setImagesData((prev) => {
        return prev
          .filter((_, index) => index !== removedIndex)
          .map((item, idx) => ({ ...item, order: idx + 1 }));
      });
    }

    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
    message.success("Hình ảnh đã được xóa");
  };

  const containerStyle = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        {/* Danh sách ảnh */}
        {fileList.map((file) => (
          <div
            key={file.uid}
            style={containerStyle}
            className="group relative border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50 shrink-0"
          >
            {file.status === "uploading" ? (
              <Progress
                percent={file.percent}
                status="active"
                size="small"
                showInfo={false}
                className="w-[80%] m-0"
              />
            ) : (
              <>
                <img
                  src={
                    file.url ||
                    file.thumbUrl ||
                    (file.preview as string) ||
                    (file.originFileObj
                      ? URL.createObjectURL(file.originFileObj as Blob)
                      : "")
                  }
                  alt="thumbnail"
                  className="w-full h-full object-cover shrink-0"
                />
                {!isDisabled && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <Button
                      icon={<EyeOutlined />}
                      size="small"
                      className="absolute top-1 left-1 bg-black bg-opacity-50 border-none text-white rounded-full w-6 h-6 flex items-center justify-center min-w-0"
                      onClick={() => handlePreview(file)}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      className="absolute top-1 right-1 bg-red-600 bg-opacity-70 border-none text-white rounded-full w-6 h-6 flex items-center justify-center min-w-0"
                      onClick={() => handleRemove(file)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {/* Nút upload */}
        {!isDisabled && fileList.length < maxCount && (
          <Upload
            showUploadList={false}
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            onRemove={handleRemove}
            multiple={maxCount > 1}
            className="w-fit"
            beforeUpload={async (file) => {
              const totalCount =
                uploadingCountRef.current + (imagesData?.length || 0);

              if (totalCount >= maxCount) {
                if (!shownErrorRef.current) {
                  showAlertNotification({
                    type: "error",
                    title: "Tải hình ảnh thất bại",
                    message: `Bạn chỉ được tải tối đa ${maxCount} hình.`,
                  });
                  shownErrorRef.current = true;
                }
                return Upload.LIST_IGNORE;
              }

              uploadingCountRef.current += 1;
              const isImage = [
                "image/jpeg",
                "image/png",
                "image/jpg",
                "image/gif",
                "image/bmp",
                "image/webp",
              ].includes(file.type);
              const isLt2M = file.size / 1024 / 1024 <= 2;

              if (!isImage || !isLt2M) {
                if (!shownErrorRef.current) {
                  showAlertNotification({
                    type: "error",
                    title: "Tải hình ảnh thất bại",
                    message: !isImage
                      ? "Định dạng không hợp lệ. Chỉ chấp nhận các định dạng: jpg, jpeg, png, gif, bmp, webp."
                      : "Kích thước hình ảnh phải nhỏ hơn hoặc bằng 2MB.",
                  });
                  shownErrorRef.current = true;
                }
                shownErrorRef.current = false;
                uploadingCountRef.current -= 1;
                return Upload.LIST_IGNORE;
              }
              shownErrorRef.current = false;
              try {
                const fileName = `${Date.now()}-${file.name}`;
                const fileType = file.type;

                const { preSignedURL, fileUrl } = await getPresignedUrl({
                  fileName,
                  fileType,
                }).unwrap();
                const newFileItem: UploadFile = {
                  uid: file.uid,
                  name: file.name,
                  status: "uploading",
                  percent: 0,
                  originFileObj: file,
                };
                setFileList((prev) => [...prev, newFileItem]);

                await uploadToS3({
                  preSignedURL,
                  file,
                  onUploadProgress: (event: AxiosProgressEvent) => {
                    if (event.total) {
                      const percent = Math.round(
                        (event.loaded * 100) / event.total,
                      );
                      setFileList((prevList) =>
                        prevList.map((item) =>
                          item.uid === file.uid
                            ? {
                                ...item,
                                percent,
                                status: percent === 100 ? "done" : "uploading",
                              }
                            : item,
                        ),
                      );
                    }
                  },
                }).unwrap();

                const uploadedImageUrl = fileUrl.split("?")[0];

                setImagesData((prev) => [
                  ...prev,
                  {
                    filePath: uploadedImageUrl,
                    name: fileName,
                    order: prev.length + 1,
                  },
                ]);
              } catch (error) {
                console.error("Upload failed:", error);
                message.error("Tải hình ảnh thất bại");
                setFileList((prevList) =>
                  prevList.map((item) =>
                    item.uid === file.uid ? { ...item, status: "error" } : item,
                  ),
                );
              } finally {
                uploadingCountRef.current -= 1;
              }

              return Upload.LIST_IGNORE;
            }}
          >
            <div
              className={`
                flex items-center justify-center gap-1.5 
                px-3 py-2 h-[36px] w-fit
                bg-[#F5F5F5] hover:bg-gray-200 
                text-[#1D3557] font-medium text-[13px]
                transition-all whitespace-nowrap rounded-md
                border border-transparent active:scale-95
                ${loading ? "opacity-70 pointer-events-none" : "cursor-pointer"}
              `}
            >
              {loading ? (
                <CircleNotch size={16} className="animate-spin text-blue-500" />
              ) : (
                <span className="flex items-center justify-center">
                  <UploadSimpleIcon size={16} weight="bold" />
                </span>
              )}
              <span className="text-[14px] font-medium">{label}</span>
            </div>
          </Upload>
        )}
      </div>

      <Modal open={previewOpen} footer={null} onCancel={handleCancel} centered>
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </>
  );
};

export default UploadImageButton;
